
import Papa from 'papaparse';
import { supabase } from "@/integrations/supabase/client";

export interface CsvClient {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export const parseCsvFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Return the raw data so the importing component can handle mapping
        resolve(results.data as any[]);
      },
      error: (error) => {
        reject(error);
      }
    });
  });
};

export const importClients = async (clients: CsvClient[]) => {
  // Validate required fields
  const validClients = clients.filter(client => 
    client.first_name && client.first_name.trim() !== "" && 
    client.last_name && client.last_name.trim() !== ""
  );
  
  if (validClients.length === 0) {
    throw new Error("No valid clients to import. First name and last name are required.");
  }

  // Get the current user's ID
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Clean and format the data
  const formattedClients = validClients.map(client => ({
    ...client,
    // Standardize phone numbers if present
    phone: client.phone ? formatPhoneNumber(client.phone) : undefined,
    // Process other fields as needed
    user_id: user.id,
    status: 'active' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }));

  // Insert into database
  const { error } = await supabase.from('clients').insert(formattedClients);

  if (error) throw error;
  
  return validClients.length;
};

// Helper function to standardize phone number format
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // If it's a standard US number (10 digits)
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }
  // If it already has a country code
  else if (digitsOnly.length > 10) {
    return `+${digitsOnly}`;
  }
  
  // Return as is if we can't standardize
  return phone;
};

// Platform-specific field mappings
export const platformMappings = {
  squire: {
    'First Name': 'first_name',
    'Last Name': 'last_name',
    'Email': 'email',
    'Phone Number': 'phone',
    'Client Notes': 'notes'
  },
  glossgenius: {
    'First Name': 'first_name',
    'Last Name': 'last_name',
    'Email Address': 'email',
    'Phone': 'phone',
    'Notes': 'notes'
  },
  vagaro: {
    'First': 'first_name',
    'Last': 'last_name',
    'Email': 'email',
    'Mobile Phone': 'phone',
    'Client Notes': 'notes'
  }
};
